import { MdBalance, MdOutlineCategory, MdOutlineLayers, MdFilterTiltShift, MdOutlineGroups2 } from 'react-icons/md';
import graph from '../../../assets/icons/graph_2.svg'
import swap from '../../../assets/icons/swap_driving_apps_wheel.svg'
import { IOptions } from '../../../interfaces/Rights';
import { ISelectedItem } from '../../../components/Inputs/Selects/MultiSelectCheck';
import { DraftColumnsProps, FilterColumnProps, IndependentColumnProps } from '../components/SearchAndFilters/types';
import { useAppSelector } from '../../../redux/hooks';
import { isOnlyStructural } from '../utils/filterDraft';

const toSelectedItem = (option: IOptions, label: string): ISelectedItem => ({
    label,
    value: option.value,
    id: option.id,
});

const allValues = (options: IOptions[], checked: boolean) =>
    checked ? options.map(o => o.value) : [];

const allItems = (options: IOptions[], label: string, checked: boolean) =>
    checked ? options.map(o => toSelectedItem(o, label)) : [];

export const useFilterColumns = ({ draft, toggleField, setField }: DraftColumnsProps) => {
    // Opciones de cada columna
    const filterState = useAppSelector((state) => state.indicator_display_filter);
    const rightOptions = filterState.rightOptions ?? [];
    const indicatorTypesOptions = filterState.indicatorTypesOptions;
    const dimensionOptions = filterState.dimensionOptions ?? [];
    const subDimensionOptions = filterState.subDimensionOptions ?? [];
    const indicatorsOptions = filterState.indicatorsOptions ?? [];
    const odsOptions = filterState.ODSOptions ?? [];
    const priorityGroupsOptions = filterState.priorityGroupsOptions ?? [];

    // Columnas en cadena: cada una se habilita (visible) cuando la anterior tiene selecciones
    const chainColumns: FilterColumnProps[] = [
        {
            title: 'Derechos',
            icon: MdBalance,
            options: rightOptions,
            selectedValues: draft.rights,
            onSelect: (option) => toggleField('rights')(option.value),
            onSelectAll: (checked) => setField('rights')(allValues(rightOptions, checked)),
            visible: true,
            noOptionsMessage: 'No se encontraron derechos',
        },
        {
            title: 'Tipo de indicadores',
            icon: MdOutlineCategory,
            options: indicatorTypesOptions,
            selectedValues: draft.types,
            onSelect: (option) => toggleField('types')(option.value),
            onSelectAll: (checked) => setField('types')(allValues(indicatorTypesOptions, checked)),
            visible: draft.rights.length > 0,
            emptyMessage: 'Selecciona un derecho para continuar',
            noOptionsMessage: 'No se encontraron tipos de indicador',
        },
        {
            title: 'Dimensiones',
            icon: MdOutlineLayers,
            options: dimensionOptions,
            selectedValues: draft.dimensions.map(s => s.value),
            onSelect: (option) => toggleField('dimensions')(toSelectedItem(option, 'Dimensión')),
            onSelectAll: (checked) => setField('dimensions')(allItems(dimensionOptions, 'Dimensión', checked)),
            visible: draft.types.length > 0,
            emptyMessage: 'Selecciona un tipo de indicador para continuar',
            noOptionsMessage: 'No se encontraron dimensiones',
        },
        {
            title: 'Subdimensiones',
            icon: graph,
            options: subDimensionOptions,
            selectedValues: draft.subdimensions.map(s => s.value),
            onSelect: (option) => toggleField('subdimensions')(toSelectedItem(option, 'Subdimensión')),
            onSelectAll: (checked) => setField('subdimensions')(allItems(subDimensionOptions, 'Subdimensión', checked)),
            visible: draft.dimensions.length > 0,
            emptyMessage: 'Selecciona una dimensión para continuar',
            noOptionsMessage: 'No se encontraron subdimensiones',
        },
        {
            title: 'Indicadores',
            icon: swap,
            options: indicatorsOptions,
            selectedValues: draft.indicators.map(s => s.value),
            onSelect: (option) => toggleField('indicators')(toSelectedItem(option, 'Indicadores')),
            onSelectAll: (checked) => setField('indicators')(allItems(indicatorsOptions, 'Indicadores', checked)),
            visible: draft.subdimensions.length > 0,
            emptyMessage: 'Selecciona una subdimensión para continuar',
            noOptionsMessage: 'No hay indicadores que coincidan con los filtros seleccionados. Probá con otro período o modificá los filtros para ampliar la búsqueda.',
            showArrow: false,
        },
    ];

    // Columnas independientes de la cadena 
    const independentColumns: IndependentColumnProps[] = [
        {
            title: 'ODS',
            icon: MdFilterTiltShift,
            options: odsOptions.map((ods) => ({ value: ods.value, id: ods.id ?? 0, label: ods.value })),
            selectedValues: draft.ods.map(s => s.value),
            selectedItems: draft.ods,
            onSelect: (option) => toggleField('ods')(toSelectedItem(option, 'ODS')),
            onSelectAll: (checked) => { setField('ods')(allItems(odsOptions, 'ODS', checked)) },
            // los indicadores estructurales no tienen ODS
            visible: !isOnlyStructural(draft.types),
            emptyMessage: 'Los indicadores estructurales no tienen ODS',
            noOptionsMessage: 'No se encontraron ODS',
            showArrow: false,
        },
        {
            title: 'Grupos prioritarios',
            icon: MdOutlineGroups2,
            options: priorityGroupsOptions,
            selectedValues: draft.priorityGroups.map(s => s.value),
            selectedItems: draft.priorityGroups,
            onSelect: (option) => toggleField('priorityGroups')(toSelectedItem(option, 'Grupos prioritarios')),
            onSelectAll: (checked) => { setField('priorityGroups')(allItems(priorityGroupsOptions, 'Grupos prioritarios', checked)) },
            visible: true,
            noOptionsMessage: 'No se encontraron grupos prioritarios',
            showArrow: false,
        },
    ];

    return { chainColumns, independentColumns };
}